/**
 * A helper card on Billing, folded to one line.
 *
 * Owner, Oct 26 2026: "cleanup billing — … the reminder texts should be
 * collapsed, same with the make-the-mow-free feature." Each helper shows one
 * line that says what is inside ("3 open bills, oldest 18 days") and opens on
 * a tap; its ticks, buttons and results live inside and work as before. The
 * fold is remembered per card on this device (localStorage, never required),
 * folded by default. Nothing here sends anything: every send keeps its own
 * confirm inside the card.
 */
import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export const readFold = (key: string, fallback = false): boolean => {
  try { const v = localStorage.getItem(key); return v === null ? fallback : v === '1'; } catch { return fallback; }
};
export const writeFold = (key: string, open: boolean) => {
  try { localStorage.setItem(key, open ? '1' : '0'); } catch { /* private window: just this visit */ }
};

export default function FoldCard({ storageKey, title, summary, icon, tone = 'plain', forceOpen, children }: {
  /** localStorage key for this card's fold ('billing_fold_reminders'). */
  storageKey: string;
  /** The card's name — "Payment reminders". */
  title: React.ReactNode;
  /** The one line that says what is inside — "3 open bills, oldest 18 days". */
  summary: React.ReactNode;
  icon?: React.ReactNode;
  tone?: 'plain' | 'brand' | 'amber';
  /** A link lands here wanting the card open (Billing?review=…): opens it once, whatever was remembered. */
  forceOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(() => readFold(storageKey, false));
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  const toggle = () => setOpen(o => { writeFold(storageKey, !o); return !o; });
  const border = tone === 'brand' ? 'border border-brand-300' : tone === 'amber' ? 'border border-amber-200' : '';
  return (
    <div className={`card ${open ? 'p-4 space-y-3' : 'p-0'} ${border}`}>
      <button type="button" onClick={toggle} aria-expanded={open}
        className={`w-full flex items-center justify-between gap-3 text-left ${open ? '' : 'px-4 py-3'}`}>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-gray-900 inline-flex items-center gap-1.5">{icon}{title}</p>
          <p className={`text-xs text-gray-600 ${open ? '' : 'truncate'}`}>{summary}</p>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && children}
    </div>
  );
}
